import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import messService from '../services/messService';
import { formatDateTimeIST } from '../utils/istTimeUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const isSubscriptionExpired = (expiryDateStr) => {
  if (!expiryDateStr) return false;
  const expStr = expiryDateStr.substring(0, 10);
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;
  return expStr < todayStr;
};

const isSubscriptionUpcoming = (joiningDateStr) => {
  if (!joiningDateStr) return false;
  const startStr = joiningDateStr.substring(0, 10);
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const todayStr = `${year}-${month}-${day}`;
  return startStr > todayStr;
};

const MessPage = () => {
  // Read initial tab from URL hash (e.g., /mess#memberships)
  const getInitialTab = () => {
    const hash = window.location.hash.slice(1); // Remove the # prefix
    const validTabs = ['messes', 'memberships', 'menu', 'leaves', 'bookings'];
    return validTabs.includes(hash) ? hash : 'messes';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [messes, setMesses] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [messHistory, setMessHistory] = useState([]);
  const [messHistoryLoading, setMessHistoryLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState(null); // 'all' | 'active' | 'ended' | 'pending'
  const [messLeaveSearch, setMessLeaveSearch] = useState('');
  const [messBookingSearch, setMessBookingSearch] = useState('');

  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingMessId, setEditingMessId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    capacity: '',
    monthly_fee: '',
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (showEditForm) {
          setShowEditForm(false);
        } else if (showAddForm) {
          setShowAddForm(false);
        } else if (showConfirmModal) {
          setShowConfirmModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showEditForm, showAddForm, showConfirmModal]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log(`Loading data for tab: ${activeTab}`);
      if (activeTab === 'messes') {
        const response = await messService.getMessesAdmin();
        console.log('Messes response:', response);
        setMesses(response.data?.data || []);
      } else if (activeTab === 'memberships') {
        // Load both memberships and leaves for calculating extensions
        const [membershipsResponse, leavesResponse] = await Promise.all([
          messService.getMembershipsAdmin(),
          messService.getLeavesAdmin()
        ]);
        console.log('Memberships response:', membershipsResponse);
        console.log('Leaves response:', leavesResponse);
        const membershipsData = membershipsResponse.data?.data || [];
        const leavesData = leavesResponse.data?.data || [];
        
        // Log memberships and leaves for debugging
        console.log(`Loaded ${membershipsData.length} memberships and ${leavesData.length} leaves`);
        
        const sortByUpdatedAt = (arr) => [...arr].sort((a, b) => {
          const aTime = a.updated_at || a.created_at || '';
          const bTime = b.updated_at || b.created_at || '';
          return bTime.localeCompare(aTime);
        });
        setMemberships(sortByUpdatedAt(membershipsData));
        setLeaves(sortByUpdatedAt(leavesData));
      } else if (activeTab === 'menu') {
        const response = await messService.getMenuItemsAdmin();
        console.log('Menu response:', response);
        setMenuItems(response.data?.data || []);
      } else if (activeTab === 'leaves') {
        const response = await messService.getLeavesAdmin();
        console.log('Leaves response:', response);
        const leavesData = response.data?.data || [];
        setLeaves([...leavesData].sort((a, b) => {
          const aTime = a.updated_at || a.created_at || '';
          const bTime = b.updated_at || b.created_at || '';
          return bTime.localeCompare(aTime);
        }));
      } else if (activeTab === 'bookings') {
        const response = await messService.getBookingRequestsAdmin();
        console.log('Booking requests response:', response);
        const bookingsData = response.data?.data || [];
        setBookingRequests([...bookingsData].sort((a, b) => {
          const aTime = a.updated_at || a.created_at || '';
          const bTime = b.updated_at || b.created_at || '';
          return bTime.localeCompare(aTime);
        }));
      }
      setError(null);
    } catch (err) {
      console.error('Full error object:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load data';
      setError(`Failed to load data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      capacity: '',
      monthly_fee: '',
    });
    setShowAddForm(true);
  };

  const handleEditClick = (mess) => {
    setFormData({
      name: mess.name || '',
      description: mess.description || '',
      address: mess.address || '',
      capacity: mess.capacity || '',
      monthly_fee: mess.monthly_fee || '',
    });
    setEditingMessId(mess.id);
    setShowEditForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddMess = async () => {
    try {
      if (!formData.name || !formData.capacity || !formData.monthly_fee) {
        alert('Please fill in all required fields');
        return;
      }

      await messService.createMess(formData);
      setShowAddForm(false);
      loadData();
      alert('Mess Created Successfully');
    } catch (err) {
      alert('Error creating mess: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdateMess = async () => {
    try {
      if (!formData.name || !formData.capacity || !formData.monthly_fee) {
        alert('Please fill in all required fields');
        return;
      }

      await messService.updateMess(editingMessId, formData);
      setShowEditForm(false);
      loadData();
      alert('Mess Updated Successfully');
    } catch (err) {
      alert('Error updating mess: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteMess = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await messService.deleteMess(id);
        loadData();
        alert('Mess Deleted Successfully');
      } catch (err) {
        alert('Error deleting mess: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleLeaveAction = async (id, action) => {
    try {
      await messService.updateLeaveAdmin(id, { status: action });
      loadData();
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      alert('Failed to update leave status');
    }
  };

  const handleBookingAction = async (id, status) => {
    try {
      await messService.updateBookingStatus(id, { status });
      loadData();
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      alert('Failed to update booking status');
    }
  };

  const handleConfirmMembership = (membership) => {
    setSelectedMembership(membership);
    setShowConfirmModal(true);
    fetchMessHistory(membership.student?.id);
  };

  const fetchMessHistory = async (studentId) => {
    if (!studentId) return;
    setMessHistoryLoading(true);
    try {
      const token = localStorage.getItem('admin_access_token') || localStorage.getItem('access_token');
      const res = await fetch(`${API_BASE_URL}/history/admin/student/${studentId}?type=mess`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setMessHistory(data.data || []);
    } catch (e) {
      console.error('Failed to load mess history', e);
    } finally {
      setMessHistoryLoading(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedMembership) return;
    
    try {
      setConfirmingId(selectedMembership.id);
      await messService.confirmMembership(selectedMembership.id);
      alert('Membership enrollment confirmed successfully!');
      setShowConfirmModal(false);
      setSelectedMembership(null);
      loadData();
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      alert('Failed to confirm membership: ' + (err.response?.data?.message || err.message));
    } finally {
      setConfirmingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getExtendedDaysForMembership = (membershipId) => {
    // Calculate total extended days from approved leaves with >= 3 days
    const memberLeaves = leaves.filter(leave => 
      leave.membership_id === membershipId &&
      leave.status === 'approved' &&
      (leave.days || 0) >= 3
    );
    
    const totalDays = memberLeaves.reduce((total, leave) => total + (leave.days || 0), 0);
    
    if (memberLeaves.length > 0) {
      console.log(`Membership ${membershipId}: Found ${memberLeaves.length} leaves, total ${totalDays} days`);
    }
    
    return totalDays;
  };

  const activeStudentIds = new Set(
    memberships
      .filter(m => m.status === 'active' && m.payment_completed && !(m.expiry_date && isSubscriptionExpired(m.expiry_date)) && !isSubscriptionUpcoming(m.joining_date))
      .map(m => m.student_id)
  );

  const filteredMemberships = (() => {
    let list = memberships;
    if (memberFilter === 'active') list = list.filter(m => m.status === 'active' && m.payment_completed && !(m.expiry_date && isSubscriptionExpired(m.expiry_date)) && !isSubscriptionUpcoming(m.joining_date));
    else if (memberFilter === 'upcoming') list = list.filter(m => m.status === 'active' && m.payment_completed && isSubscriptionUpcoming(m.joining_date));
    else if (memberFilter === 'ended') list = list.filter(m => (m.expiry_date && isSubscriptionExpired(m.expiry_date)) && !activeStudentIds.has(m.student_id));
    else if (memberFilter === 'pending') list = list.filter(m => m.status === 'pending' || (m.status === 'active' && !m.payment_completed));
    if (memberSearch.trim()) list = list.filter(m => (`${m.student?.user?.first_name || ''} ${m.student?.user?.last_name || ''}`).toLowerCase().includes(memberSearch.toLowerCase()));
    return list;
  })();

  // Build a map: student_id → their very first mess joining_date (across all memberships that are paid)
  const firstJoiningDateByStudent = {};
  memberships.forEach(m => {
    if (!m.payment_completed) return;
    const sid = m.student_id;
    const jd = m.joining_date;
    if (!sid || !jd) return;
    if (!firstJoiningDateByStudent[sid] || jd < firstJoiningDateByStudent[sid]) {
      firstJoiningDateByStudent[sid] = jd;
    }
  });

  return (
    <Layout title="Mess Management">
      <style>{`
        .tabs { display: flex; gap: 8px; margin-bottom: 24px; border-bottom: 1px solid #e5e7eb; padding-bottom: 12px; }
        .tab { padding: 10px 20px; border: none; background: none; font-size: 14px; font-weight: 500; color: #6b7280; cursor: pointer; border-radius: 12px; transition: all 0.3s ease; }
        .tab:hover { background: #f3f4f6; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1); }
        .tab.active { background: #f7fee7; color: #65a30d; box-shadow: 0 4px 12px -2px rgba(132, 204, 22, 0.2); }
        .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .btn-primary { background: #84cc16; color: white; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; }
        .btn-primary:hover { background: #65a30d; }
        .stats-row { display: flex; gap: 20px; margin-bottom: 24px; }
        .stat-card { background: #f9fafb; padding: 20px; border-radius: 16px; border: 1px solid rgba(0, 0, 0, 0.04); flex: 1; box-shadow: 0 6px 12px -2px rgba(99, 102, 241, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.3s ease; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -2px rgba(99, 102, 241, 0.2), 0 12px 24px -5px rgba(0, 0, 0, 0.08); }
        .stat-card h3 { font-size: 12px; color: #6b7280; margin: 0 0 8px; text-transform: uppercase; }
        .stat-card .value { font-size: 28px; font-weight: 600; color: #1f2937; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        .data-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; font-size: 14px; }
        .data-table tr:hover { background: #f9fafb; }
        .btn { padding: 6px 12px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500; font-size: 13px; }
        .btn-edit { background: #dbeafe; color: #1d4ed8; }
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-success { background: #d1fae5; color: #047857; }
        .btn-danger { background: #fee2e2; color: #dc2626; }
        .status-badge { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
        .status-active { background: #d1fae5; color: #047857; }
        .status-upcoming { background: #eff6ff; color: #1d4ed8; }
        .status-pending { background: #fef3c7; color: #b45309; }
        .status-approved { background: #dbeafe; color: #1d4ed8; }
        .status-rejected { background: #fee2e2; color: #dc2626; }
        .actions { display: flex; gap: 8px; }
        .loading, .error, .empty { text-align: center; padding: 48px; color: #6b7280; }
        .error { color: #dc2626; }
        .form-group { margin-bottom: 20px; }
        .form-group label { display: block; font-weight: 500; margin-bottom: 8px; color: #374151; font-size: 14px; }
        .form-group input, .form-group textarea { width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; box-sizing: border-box; }
        .form-group textarea { resize: vertical; min-height: 80px; }
        .form-actions { display: flex; gap: 12px; margin-top: 24px; }
        .btn-submit { background: #84cc16; color: white; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; }
        .btn-submit:hover { background: #65a30d; }
        .btn-cancel { background: #e5e7eb; color: #374151; padding: 10px 20px; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; }
      `}</style>

      <div className="tabs">
        <button className={`tab ${activeTab === 'messes' ? 'active' : ''}`} onClick={() => setActiveTab('messes')}>Messes</button>
        <button className={`tab ${activeTab === 'memberships' ? 'active' : ''}`} onClick={() => setActiveTab('memberships')}>Memberships</button>
        <button className={`tab ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => setActiveTab('menu')}>Menu Items</button>
        <button className={`tab ${activeTab === 'leaves' ? 'active' : ''}`} onClick={() => setActiveTab('leaves')}>Leave Requests</button>
        <button className={`tab ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>Booking Requests</button>
      </div>

      {activeTab === 'messes' && (
        <>
          <div className="header-row">
            <h3 style={{ margin: 0 }}>Manage Messes</h3>
            <button className="btn-primary" onClick={handleAddClick}>+ Add New Mess</button>
          </div>

          <div className="stats-row">
            <div className="stat-card">
              <h3>Total Messes</h3>
              <div className="value">{messes.length}</div>
            </div>
            <div className="stat-card">
              <h3>Active Messes</h3>
              <div className="value">{messes.filter(m => m.is_active).length}</div>
            </div>
            <div className="stat-card">
              <h3>Total Capacity</h3>
              <div className="value">{messes.reduce((sum, m) => sum + (m.capacity || 0), 0)}</div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Description</th><th>Address</th><th>Capacity</th><th>Monthly Fee</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {messes.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center' }}>No messes found</td></tr>
                ) : messes.map((mess) => (
                  <tr key={mess.id}>
                    <td><strong>{mess.name}</strong></td>
                    <td>{mess.description || 'N/A'}</td>
                    <td>{mess.address || 'N/A'}</td>
                    <td>{mess.capacity}</td>
                    <td>₹{mess.monthly_fee}</td>
                    <td><span className={`status-badge ${mess.is_active ? 'status-active' : 'status-pending'}`}>{mess.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="actions">
                        <button className="btn btn-edit" onClick={() => handleEditClick(mess)}>Edit</button>
                        <button className="btn btn-delete" onClick={() => handleDeleteMess(mess.id, mess.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {activeTab === 'memberships' && (
        <>
          {/* Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div
              onClick={() => setMemberFilter(prev => prev === 'all' ? null : 'all')}
              style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: memberFilter === 'all' ? '2px solid #0369a1' : '1px solid #bae6fd', cursor: 'pointer', boxShadow: memberFilter === 'all' ? '0 0 0 3px rgba(3,105,161,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
            >
              {memberFilter === 'all' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#0369a1', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
              <div style={{ fontSize: '12px', color: '#0369a1', fontWeight: '600', marginBottom: '4px' }}>Total Memberships</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#0c4a6e' }}>{memberships.length}</div>
            </div>
            <div
              onClick={() => setMemberFilter(prev => prev === 'active' ? null : 'active')}
              style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: memberFilter === 'active' ? '2px solid #166534' : '1px solid #bbf7d0', cursor: 'pointer', boxShadow: memberFilter === 'active' ? '0 0 0 3px rgba(22,101,52,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
            >
              {memberFilter === 'active' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#166534', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
              <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>Active Members</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#15803d' }}>{memberships.filter(m => m.status === 'active' && m.payment_completed && !(m.expiry_date && isSubscriptionExpired(m.expiry_date)) && !isSubscriptionUpcoming(m.joining_date)).length}</div>
            </div>
            <div
              onClick={() => setMemberFilter(prev => prev === 'upcoming' ? null : 'upcoming')}
              style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', border: memberFilter === 'upcoming' ? '2px solid #2563eb' : '1px solid #bfdbfe', cursor: 'pointer', boxShadow: memberFilter === 'upcoming' ? '0 0 0 3px rgba(37,99,235,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
            >
              {memberFilter === 'upcoming' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#2563eb', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
              <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', marginBottom: '4px' }}>Upcoming Members</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e3a8a' }}>{memberships.filter(m => m.status === 'active' && m.payment_completed && isSubscriptionUpcoming(m.joining_date)).length}</div>
            </div>
            <div
              onClick={() => setMemberFilter(prev => prev === 'ended' ? null : 'ended')}
              style={{ padding: '16px', background: '#fef2f2', borderRadius: '8px', border: memberFilter === 'ended' ? '2px solid #dc2626' : '1px solid #fecaca', cursor: 'pointer', boxShadow: memberFilter === 'ended' ? '0 0 0 3px rgba(220,38,38,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
            >
              {memberFilter === 'ended' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#dc2626', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
              <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: '600', marginBottom: '4px' }}>Subscription Ended</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#b91c1c' }}>{memberships.filter(m => (m.expiry_date && isSubscriptionExpired(m.expiry_date)) && !activeStudentIds.has(m.student_id)).length}</div>
            </div>
            <div
              onClick={() => setMemberFilter(prev => prev === 'pending' ? null : 'pending')}
              style={{ padding: '16px', background: '#fffbeb', borderRadius: '8px', border: memberFilter === 'pending' ? '2px solid #92400e' : '1px solid #fde68a', cursor: 'pointer', boxShadow: memberFilter === 'pending' ? '0 0 0 3px rgba(146,64,14,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
            >
              {memberFilter === 'pending' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#92400e', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
              <div style={{ fontSize: '12px', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>Pending</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#b45309' }}>{memberships.filter(m => m.status === 'pending' || (m.status === 'active' && !m.payment_completed)).length}</div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <div className="table-container">
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', paddingRight: '16px' }}>
              <input
                type="text"
                placeholder="Search by student name..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '260px' }}
              />
              {memberSearch && <button onClick={() => setMemberSearch('')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px' }}>✕</button>}
              {memberFilter && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 10px' }}>
                  {memberFilter === 'all' && 'All Memberships'}
                  {memberFilter === 'active' && 'Active Members'}
                  {memberFilter === 'upcoming' && 'Upcoming Members'}
                  {memberFilter === 'ended' && 'Subscription Ended'}
                  {memberFilter === 'pending' && 'Pending'}
                  <button onClick={() => setMemberFilter(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '14px', padding: '0 2px' }}>✕</button>
                </span>
              )}
              <span style={{ fontSize: '12px', color: '#666' }}>
                Showing {filteredMemberships.length} of {memberships.length}
              </span>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Student</th>
                  <th>Mess</th>
                  <th>Plan</th>
                  <th>Joining Date</th>
                  <th>Subscription Starts</th>
                  <th>Subscription Ends</th>
                  <th>Payment Status</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMemberships.length === 0 ? (
                  <tr><td colSpan="10" style={{ textAlign: 'center' }}>No memberships found</td></tr>
                ) : filteredMemberships.map((m, index) => {
                  const extendedDays = getExtendedDaysForMembership(m.id);
                  return (
                  <tr key={m.id}>
                    <td>{index + 1}</td>
                    <td><strong>{m.student?.user?.first_name} {m.student?.user?.last_name}</strong></td>
                    <td>{m.mess?.name || 'N/A'}</td>
                    <td>{m.plan_type || 'N/A'}</td>
                    <td>{firstJoiningDateByStudent[m.student_id] ? formatDate(firstJoiningDateByStudent[m.student_id]) : '—'}</td>
                    <td style={{ color: m.payment_completed ? '#2563eb' : '#6b7280', fontWeight: '500' }}>
                      {m.payment_completed ? formatDate(m.joining_date) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ color: m.payment_completed ? '#ea580c' : '#6b7280', fontWeight: '500' }}>
                          {m.payment_completed ? formatDate(m.expiry_date) : '—'}
                        </span>
                        {m.expiry_date && isSubscriptionExpired(m.expiry_date) && (
                          <span style={{
                            background: '#fef2f2',
                            color: '#dc2626',
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: '1px solid #fecaca',
                            display: 'inline-block'
                          }}>Subscription Ended</span>
                        )}
                        {extendedDays > 0 && (
                          <span style={{
                            fontSize: '11px',
                            color: '#15803d',
                            background: '#f0fdf4',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: '1px solid #bbf7d0',
                            display: 'inline-block'
                          }}>
                            +{extendedDays} {extendedDays === 1 ? 'day' : 'days'} (leave)
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${m.payment_completed ? 'status-active' : 'status-pending'}`}>
                        {m.payment_completed ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${m.status === 'active' && isSubscriptionUpcoming(m.joining_date) ? 'upcoming' : m.status}`}>
                        {m.status === 'active' && isSubscriptionUpcoming(m.joining_date) ? 'upcoming' : m.status}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        {m.payment_completed && m.status !== 'active' && m.status !== 'cancelled' && m.status !== 'left' && m.status !== 'inactive' && (
                          <button 
                            className="btn btn-success"
                            onClick={() => handleConfirmMembership(m)}
                            disabled={confirmingId === m.id}
                          >
                            {confirmingId === m.id ? 'Confirming...' : 'Confirm'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}


      {activeTab === 'menu' && (
        <>
          <div className="stats-row">
            <div className="stat-card">
              <h3>Menu Items</h3>
              <div className="value">{menuItems.length}</div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Item Name</th><th>Category</th><th>Day</th><th>Meal Type</th></tr>
              </thead>
              <tbody>
                {menuItems.length === 0 ? (
                  <tr><td colSpan="4" style={{ textAlign: 'center' }}>No menu items found</td></tr>
                ) : menuItems.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.name}</strong></td>
                    <td>{item.category}</td>
                    <td>{item.day}</td>
                    <td>{item.meal_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {activeTab === 'leaves' && (
        <>
          <div className="stats-row">
            <div className="stat-card">
              <h3>Pending Leaves</h3>
              <div className="value">{leaves.filter(l => l.status === 'pending').length}</div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <div className="table-container">
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', paddingRight: '16px' }}>
              <input
                type="text"
                placeholder="Search by student name..."
                value={messLeaveSearch}
                onChange={(e) => setMessLeaveSearch(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '260px' }}
              />
              {messLeaveSearch && <button onClick={() => setMessLeaveSearch('')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px' }}>✕</button>}
              <span style={{ fontSize: '12px', color: '#666' }}>
                Showing {leaves.filter(l => !messLeaveSearch.trim() || (`${l.membership?.student?.user?.first_name || ''} ${l.membership?.student?.user?.last_name || ''}`).toLowerCase().includes(messLeaveSearch.toLowerCase())).length} of {leaves.length}
              </span>
            </div>
            <table className="data-table">
              <thead>
                <tr><th>Sr. No.</th><th>Student</th><th>From</th><th>To</th><th>Reason</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {leaves.filter(l => !messLeaveSearch.trim() || (`${l.membership?.student?.user?.first_name || ''} ${l.membership?.student?.user?.last_name || ''}`).toLowerCase().includes(messLeaveSearch.toLowerCase())).length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center' }}>No leave requests found</td></tr>
                ) : leaves.filter(l => !messLeaveSearch.trim() || (`${l.membership?.student?.user?.first_name || ''} ${l.membership?.student?.user?.last_name || ''}`).toLowerCase().includes(messLeaveSearch.toLowerCase())).map((leave, index) => (
                  <tr key={leave.id}>
                    <td>{index + 1}</td>
                    <td><strong>{leave.membership?.student?.user?.first_name} {leave.membership?.student?.user?.last_name}</strong></td>
                    <td>{formatDate(leave.leave_from)}</td>
                    <td>{formatDate(leave.leave_until)}</td>
                    <td>{leave.reason || 'N/A'}</td>
                    <td><span className={`status-badge status-${leave.status}`}>{leave.status}</span></td>
                    <td>
                      {leave.status === 'pending' && (
                        <div className="actions">
                          <button className="btn btn-success" onClick={() => handleLeaveAction(leave.id, 'approved')}>Approve</button>
                          <button className="btn btn-danger" onClick={() => handleLeaveAction(leave.id, 'rejected')}>Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'bookings' && (
        <>
          <div className="stats-row">
            <div className="stat-card">
              <h3>Pending Bookings</h3>
              <div className="value">{bookingRequests.filter(b => b.status === 'pending').length}</div>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <div className="table-container">
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', paddingRight: '16px' }}>
                <input
                  type="text"
                  placeholder="Search by student name or phone..."
                  value={messBookingSearch}
                  onChange={(e) => setMessBookingSearch(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', minWidth: '260px' }}
                />
                {messBookingSearch && <button onClick={() => setMessBookingSearch('')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px' }}>✕</button>}
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Showing {bookingRequests.filter(b => !messBookingSearch.trim() || (b.student_name || '').toLowerCase().includes(messBookingSearch.toLowerCase()) || (b.student_phone || '').toLowerCase().includes(messBookingSearch.toLowerCase())).length} of {bookingRequests.length}
                </span>
              </div>
              <table className="data-table">
                <thead>
                  <tr><th>Sr. No.</th><th>Student</th><th>Mess</th><th>Plan</th><th>Phone</th><th>Requested Date & Time</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {bookingRequests.filter(b => !messBookingSearch.trim() || (b.student_name || '').toLowerCase().includes(messBookingSearch.toLowerCase()) || (b.student_phone || '').toLowerCase().includes(messBookingSearch.toLowerCase())).length === 0 ? (
                    <tr><td colSpan="8" style={{ textAlign: 'center' }}>No booking requests found</td></tr>
                  ) : bookingRequests.filter(b => !messBookingSearch.trim() || (b.student_name || '').toLowerCase().includes(messBookingSearch.toLowerCase()) || (b.student_phone || '').toLowerCase().includes(messBookingSearch.toLowerCase())).map((booking, index) => (
                    <tr key={booking.id}>
                      <td>{index + 1}</td>
                      <td>{booking.student_name}</td>
                      <td>{booking.mess_name}</td>
                      <td>{booking.plan_type}</td>
                      <td>{booking.student_phone}</td>
                      <td>{formatDateTimeIST(booking.created_at)}</td>
                      <td><span className={`status-badge status-${booking.status}`}>{booking.status}</span></td>
                      <td className="actions">
                        {booking.status === 'pending' && (
                          <>
                            <button className="btn btn-success" onClick={() => handleBookingAction(booking.id, 'approved')}>Approve</button>
                            <button className="btn btn-danger" onClick={() => handleBookingAction(booking.id, 'rejected')}>Reject</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Modal isOpen={showAddForm} title="Add New Mess" onClose={() => setShowAddForm(false)}>
        <div className="form-group">
          <label>Mess Name *</label>
          <input 
            type="text" 
            name="name" 
            value={formData.name} 
            onChange={handleFormChange}
            placeholder="Enter mess name"
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea 
            name="description" 
            value={formData.description} 
            onChange={handleFormChange}
            placeholder="Enter mess description"
          />
        </div>
        <div className="form-group">
          <label>Address</label>
          <textarea 
            name="address" 
            value={formData.address} 
            onChange={handleFormChange}
            placeholder="Enter mess address"
          />
        </div>
        <div className="form-group">
          <label>Capacity *</label>
          <input 
            type="number" 
            name="capacity" 
            value={formData.capacity} 
            onChange={handleFormChange}
            placeholder="Enter capacity"
          />
        </div>
        <div className="form-group">
          <label>Monthly Fee *</label>
          <input 
            type="number" 
            name="monthly_fee" 
            value={formData.monthly_fee} 
            onChange={handleFormChange}
            placeholder="Enter monthly fee"
          />
        </div>
        <div className="form-actions">
          <button className="btn-submit" onClick={handleAddMess}>Create Mess</button>
          <button className="btn-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
        </div>
      </Modal>

      <Modal isOpen={showEditForm} title="Edit Mess" onClose={() => setShowEditForm(false)}>
        <div className="form-group">
          <label>Mess Name *</label>
          <input 
            type="text" 
            name="name" 
            value={formData.name} 
            onChange={handleFormChange}
            placeholder="Enter mess name"
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea 
            name="description" 
            value={formData.description} 
            onChange={handleFormChange}
            placeholder="Enter mess description"
          />
        </div>
        <div className="form-group">
          <label>Address</label>
          <textarea 
            name="address" 
            value={formData.address} 
            onChange={handleFormChange}
            placeholder="Enter mess address"
          />
        </div>
        <div className="form-group">
          <label>Capacity *</label>
          <input 
            type="number" 
            name="capacity" 
            value={formData.capacity} 
            onChange={handleFormChange}
            placeholder="Enter capacity"
          />
        </div>
        <div className="form-group">
          <label>Monthly Fee *</label>
          <input 
            type="number" 
            name="monthly_fee" 
            value={formData.monthly_fee} 
            onChange={handleFormChange}
            placeholder="Enter monthly fee"
          />
        </div>
        <div className="form-actions">
          <button className="btn-submit" onClick={handleUpdateMess}>Update Mess</button>
          <button className="btn-cancel" onClick={() => setShowEditForm(false)}>Cancel</button>
        </div>
      </Modal>

      <Modal isOpen={showConfirmModal} title="Confirm Membership Enrollment" onClose={() => {
        setShowConfirmModal(false);
        setSelectedMembership(null);
      }}>
        {selectedMembership && (
          <div>
            <p style={{ marginBottom: '12px', color: '#374151' }}>
              <strong>Student:</strong> {selectedMembership.student?.user?.first_name} {selectedMembership.student?.user?.last_name}
            </p>
            <p style={{ marginBottom: '12px', color: '#374151' }}>
              <strong>Mess:</strong> {selectedMembership.mess?.name}
            </p>
            <p style={{ marginBottom: '12px', color: '#374151' }}>
              <strong>Plan:</strong> {selectedMembership.plan_type}
            </p>
            <p style={{ marginBottom: '12px', color: '#374151' }}>
              <strong>Payment Status:</strong> {selectedMembership.payment_completed ? 'Verified' : 'Pending'}
            </p>
            
            <hr style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />
            
            <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
              Confirming this enrollment will activate the membership and set the joining date to today. The student will receive a notification.
            </p>

            {/* Mess Admission History */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#1e3a5f', borderBottom: '2px solid #e5e7eb', paddingBottom: '6px' }}>
                📋 Mess History
              </h4>
              {messHistoryLoading ? (
                <p style={{ color: '#6b7280', fontSize: '12px' }}>Loading history…</p>
              ) : messHistory.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '12px' }}>No history records found.</p>
              ) : (
                <div style={{ overflowX: 'auto', maxHeight: '220px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '5px 7px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Event</th>
                        <th style={{ padding: '5px 7px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Period</th>
                        <th style={{ padding: '5px 7px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Amount</th>
                        <th style={{ padding: '5px 7px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Status</th>
                        <th style={{ padding: '5px 7px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Transaction ID</th>
                        <th style={{ padding: '5px 7px', textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messHistory.map((h, i) => {
                        const eventLabels = {
                          new_admission: '🍽 New Membership', renewal: '🔄 Renewal',
                          payment: '✅ Payment', payment_pending: '⏳ Pending',
                          payment_verified: '✔ Verified', cancellation: '🚫 Cancelled',
                        };
                        const formatDate = (d) => {
                          if (!d) return '—';
                          const dt = new Date(d);
                          return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
                        };
                        return (
                          <tr key={h.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                            <td style={{ padding: '5px 7px', fontWeight: '600', color: '#374151' }}>
                              {eventLabels[h.event_type] || h.event_type}
                            </td>
                            <td style={{ padding: '5px 7px', color: '#374151' }}>
                              {h.valid_from ? formatDate(h.valid_from) : '—'}
                              {h.valid_till ? ` → ${formatDate(h.valid_till)}` : ''}
                            </td>
                            <td style={{ padding: '5px 7px', color: '#374151' }}>
                              {h.rent_amount ? `₹${Number(h.rent_amount).toLocaleString()}` : '—'}
                            </td>
                            <td style={{ padding: '5px 7px' }}>
                              <span style={{
                                background: h.payment_status === 'completed' ? '#d1fae5' : '#fef3c7',
                                color: h.payment_status === 'completed' ? '#065f46' : '#92400e',
                                padding: '2px 5px', borderRadius: '4px', fontSize: '10px', fontWeight: '600'
                              }}>
                                {(h.payment_status || '—').toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '5px 7px', fontFamily: 'monospace', fontSize: '10px', color: '#4b5563' }}>
                              {h.transaction_id || '—'}
                            </td>
                            <td style={{ padding: '5px 7px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                              {formatDate(h.created_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                style={{
                  padding: '10px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedMembership(null);
                }}
              >
                Cancel
              </button>
              <button 
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  background: '#84cc16',
                  color: 'white',
                  border: 'none',
                  cursor: confirmingId === selectedMembership.id ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  opacity: confirmingId === selectedMembership.id ? 0.7 : 1
                }}
                onClick={handleConfirmAction}
                disabled={confirmingId === selectedMembership.id}
              >
                {confirmingId === selectedMembership.id ? 'Confirming...' : 'Confirm Enrollment'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

// Modal component defined outside to prevent re-creation on each render
const Modal = ({ isOpen, title, onClose, children }) => {
  if (!isOpen) return null;
  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={closeButtonStyle}>×</button>
        </div>
        <div style={modalBodyStyle}>
          {children}
        </div>
      </div>
    </div>
  );
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  width: '90%',
  maxWidth: '500px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
};

const modalBodyStyle = {
  padding: '24px',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '28px',
  cursor: 'pointer',
  color: '#6b7280',
};

export default MessPage;
